/**
 * Header-Suche – Datenquellen: WP-Suchindex (/wp-json/wp/v2/search, nach Seitensprache gefiltert) und
 * Synonym-Ebene aus kb_diagnosen (Supabase, einmal pro Sitzung geladen, im sessionStorage 24 h gecacht).
 * Nachfolger von js/pc-header-search-service.js.
 * @module components/header-search/header-search.service
 */
import { getPageLocale } from '../../shared/services/config.js';
import { select } from '../../shared/services/supabase-client.js';

const PER_PAGE = 6;
const CACHE_KEY = 'pc-header-search-diagnosen-v2';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * @typedef {{slug: string, deutscher_name: string, englischer_name: string|null,
 *   passende_artikel: string[]|null, passende_artikel_en: string[]|null}} DiagnosisSynonym
 */

/**
 * Basis-URL (window.PC_HEADER_SEARCH_BASE für Einbettungen, sonst relativ).
 * @returns {string} Basis
 */
export function searchBase() {
  return window.PC_HEADER_SEARCH_BASE || '';
}

/**
 * Volltextsuche über den WP-Suchindex.
 * @param {string} query Suchbegriff
 * @returns {Promise<Array<{id: number, title: string, url: string, subtype: string}>>} Treffer
 */
export async function searchSite(query) {
  const language = getPageLocale().language;
  const url =
    `${searchBase()}/wp-json/wp/v2/search?search=${encodeURIComponent(query)}&per_page=${PER_PAGE}` +
    `&_fields=id,title,url,subtype&lang=${language}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Suche fehlgeschlagen');
  return response.json();
}

let synonyms = null;
let pending = null;

/**
 * Bereits geladene Synonym-Liste (synchron) oder null.
 * @returns {DiagnosisSynonym[]|null} Liste
 */
export function getSynonyms() {
  return synonyms;
}

/**
 * Sitzungs-Cache lesen.
 * @returns {DiagnosisSynonym[]|null} Liste oder null
 */
function readCache() {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.ts || !data.items || Date.now() - data.ts > CACHE_TTL_MS) return null;
    return data.items;
  } catch {
    return null;
  }
}

/**
 * Lädt die Synonyme einmalig; parallele Aufrufe teilen sich eine Anfrage. Fehler sind unkritisch.
 * @returns {Promise<DiagnosisSynonym[]|null>} Liste oder null bei Fehler
 */
export function loadSynonyms() {
  if (synonyms) return Promise.resolve(synonyms);
  const cached = readCache();
  if (cached) {
    synonyms = cached;
    return Promise.resolve(synonyms);
  }
  if (!pending) {
    pending = select('kb_diagnosen', {
      select: 'slug,deutscher_name,englischer_name,passende_artikel,passende_artikel_en',
    })
      .then((items) => {
        synonyms = items || [];
        try {
          window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: synonyms }));
        } catch {
          /* sessionStorage blockiert/voll -> nur In-Memory */
        }
        return synonyms;
      })
      .catch(() => null)
      .finally(() => {
        pending = null;
      });
  }
  return pending;
}
